import type { EstadoUnidad, TipoUnidad, UnidadProyecto } from "./api";
import { detalleCelda, type PisoAnexo } from "./edificio";
import { TOKEN_ESTADO } from "./maqueta";
import { ETIQUETA_TIPO, etiquetaPiso, nombrePiso, nombreUnidad } from "./unidades";
import { ETIQUETA_ESTADO_UNIDAD } from "./ventas";

/** Proyecto (lo que ve el fiduciante de las unidades): lógica pura. */

const ORDEN_ESTADOS: EstadoUnidad[] = ["vendida", "reservada", "adjudicada", "disponible", "fuera_de_venta"];
const NOMBRE_ESTADO: Record<EstadoUnidad, [string, string]> = {
  vendida: ["vendida", "vendidas"],
  reservada: ["reservada", "reservadas"],
  adjudicada: ["adjudicada", "adjudicadas"],
  disponible: ["disponible", "disponibles"],
  fuera_de_venta: ["fuera de venta", "fuera de venta"],
};

/** "3 vendidas · 1 reservada · 8 disponibles": sólo los estados que tienen alguna, en ese orden. */
export function textoEstados(unidades: Pick<UnidadProyecto, "estado_venta">[]): string {
  return ORDEN_ESTADOS.map((e) => [e, unidades.filter((u) => u.estado_venta === e).length] as const)
    .filter(([, n]) => n > 0)
    .map(([e, n]) => `${n} ${NOMBRE_ESTADO[e][n === 1 ? 0 : 1]}`)
    .join(" · ");
}

/** Tarjeta "Proyecto" del Inicio del fiduciante: cuántos departamentos (o unidades) y cómo están. */
export function tarjetaProyecto(unidades: UnidadProyecto[]): { valor: string; detalle: string } {
  if (unidades.length === 0) return { valor: "—", detalle: "Todavía no hay unidades cargadas" };
  const deptos = unidades.filter((u) => u.tipo === "departamento");
  const valor = deptos.length > 0 ? `${deptos.length} ${deptos.length === 1 ? "departamento" : "departamentos"}` : `${unidades.length} ${unidades.length === 1 ? "unidad" : "unidades"}`;
  return { valor, detalle: textoEstados(deptos.length > 0 ? deptos : unidades) };
}

const TIPOS_PLURAL: Record<TipoUnidad, string> = { departamento: "Departamentos", cochera: "Cocheras", baulera: "Bauleras", local: "Locales", oficina: "Oficinas" };

/** Una tarjeta por tipo de unidad que haya (en el orden de siempre): cuántas y cómo están. */
export function resumenPorTipo(unidades: UnidadProyecto[]): { tipo: TipoUnidad; titulo: string; valor: string; detalle: string }[] {
  return (Object.keys(TIPOS_PLURAL) as TipoUnidad[]).flatMap((tipo) => {
    const suyas = unidades.filter((u) => u.tipo === tipo);
    return suyas.length === 0 ? [] : [{ tipo, titulo: TIPOS_PLURAL[tipo], valor: String(suyas.length), detalle: textoEstados(suyas) }];
  });
}

// ------------------------------------------------------------------ filtros (Mati, 2026-11-02: el rediseño de Proyecto)

/** `ambientes` 4 = "4 o más". `lado`: al frente o al contrafrente (como en la maqueta). */
export interface FiltroProyecto {
  ambientes: number | null;
  lado: "frente" | "contrafrente" | null;
  soloDisponibles: boolean;
}

export const SIN_FILTRO: FiltroProyecto = { ambientes: null, lado: null, soloDisponibles: false };
export const AMBIENTES_MAS = 4;

export function hayFiltro(f: FiltroProyecto): boolean {
  return f.ambientes !== null || f.lado !== null || f.soloDisponibles;
}

/** Una unidad sin ambientes (cochera) o sin lado no entra cuando se filtra por eso. */
export function coincide(u: Pick<UnidadProyecto, "ambientes" | "frente" | "estado_venta">, f: FiltroProyecto): boolean {
  if (f.soloDisponibles && u.estado_venta !== "disponible") return false;
  if (f.ambientes !== null) {
    if (!u.ambientes) return false;
    if (f.ambientes === AMBIENTES_MAS ? u.ambientes < AMBIENTES_MAS : u.ambientes !== f.ambientes) return false;
  }
  if (f.lado !== null && u.frente !== (f.lado === "frente")) return false;
  return true;
}

function etiquetaAmbientes(n: number): string {
  if (n === 1) return "Monoambiente";
  return n >= AMBIENTES_MAS ? `${AMBIENTES_MAS}+ amb.` : `${n} amb.`;
}

/** Los ambientes que hay, en orden; los de 4 o más van juntos en "4+ amb.". */
export function opcionesAmbientes(unidades: Pick<UnidadProyecto, "ambientes">[]): { valor: number; etiqueta: string }[] {
  const valores = new Set(unidades.flatMap((u) => (u.ambientes ? [Math.min(u.ambientes, AMBIENTES_MAS)] : [])));
  return [...valores].sort((a, b) => a - b).map((valor) => ({ valor, etiqueta: etiquetaAmbientes(valor) }));
}

/** Filtrar por lado tiene sentido sólo si hay unidades al frente y al contrafrente. */
export function hayLados(unidades: Pick<UnidadProyecto, "frente">[]): boolean {
  return unidades.some((u) => u.frente === true) && unidades.some((u) => u.frente === false);
}

// ------------------------------------------------------------------ disponibilidad (arriba de todo)

const ORDEN_BARRA: EstadoUnidad[] = ["disponible", "reservada", "adjudicada", "vendida", "fuera_de_venta"];

export interface SegmentoEstado {
  clave: EstadoUnidad;
  etiqueta: string;
  valor: number;
  token: string;
}

/** "9 de 20 departamentos disponibles" y la barra por estado. Sin departamentos, cuenta las unidades. */
export function disponibilidad(unidades: Pick<UnidadProyecto, "tipo" | "estado_venta">[]): { disponibles: number; detalle: string; segmentos: SegmentoEstado[] } {
  const deptos = unidades.filter((u) => u.tipo === "departamento");
  const base = deptos.length > 0 ? deptos : unidades;
  const [uno, varios] = deptos.length > 0 ? ["departamento", "departamentos"] : ["unidad", "unidades"];
  const disponibles = base.filter((u) => u.estado_venta === "disponible").length;
  return {
    disponibles,
    detalle: base.length === 1 ? `de 1 ${uno} disponible` : `de ${base.length} ${varios} disponibles`,
    segmentos: ORDEN_BARRA.map((e) => ({ clave: e, etiqueta: ETIQUETA_ESTADO_UNIDAD[e], valor: base.filter((u) => u.estado_venta === e).length, token: TOKEN_ESTADO[e] })).filter((s) => s.valor > 0),
  };
}

// ------------------------------------------------------------------ la grilla por piso

export interface TarjetaGrilla {
  id: number;
  titulo: string;
  nombre: string;
  detalle: string;
  ubicacion: string | null;
  estado: string;
  token: string;
  apagada: boolean;
  /** Cocheras y bauleras: tarjeta chica. */
  chica: boolean;
}

export interface PisoGrilla {
  piso: number;
  etiqueta: string;
  nombre: string;
  disponibles: number;
  tarjetas: TarjetaGrilla[];
}

const CHICAS: TipoUnidad[] = ["cochera", "baulera"];

function porPisoYNumero(a: UnidadProyecto, b: UnidadProyecto): number {
  return b.piso - a.piso || Number(CHICAS.includes(a.tipo)) - Number(CHICAS.includes(b.tipo)) || a.numero.localeCompare(b.numero, "es", { numeric: true });
}

function tarjeta(u: UnidadProyecto, f: FiltroProyecto): TarjetaGrilla {
  return {
    id: u.id,
    titulo: u.tipo === "departamento" ? u.numero : `${ETIQUETA_TIPO[u.tipo]} ${u.numero}`,
    nombre: nombreUnidad(u),
    detalle: detalleCelda(u),
    ubicacion: u.frente === null ? null : u.frente ? "Al frente" : "Contrafrente",
    estado: ETIQUETA_ESTADO_UNIDAD[u.estado_venta],
    token: TOKEN_ESTADO[u.estado_venta],
    apagada: !coincide(u, f),
    chica: CHICAS.includes(u.tipo),
  };
}

/** Los pisos con unidades, de arriba hacia abajo. Con filtro, lo que no coincide va apagado y los pisos sin nada no van. */
export function pisosGrilla(unidades: UnidadProyecto[], f: FiltroProyecto): PisoGrilla[] {
  const pisos = [...new Set(unidades.map((u) => u.piso))].sort((a, b) => b - a);
  return pisos.flatMap((piso) => {
    const suyas = unidades.filter((u) => u.piso === piso).sort(porPisoYNumero);
    if (hayFiltro(f) && !suyas.some((u) => coincide(u, f))) return [];
    return [{ piso, etiqueta: etiquetaPiso(piso), nombre: nombrePiso(piso), disponibles: suyas.filter((u) => u.estado_venta === "disponible").length, tarjetas: suyas.map((u) => tarjeta(u, f)) }];
  });
}

/** Para pasar de una ficha a la otra: en el orden de la grilla, sólo las que coinciden (todas, si la elegida no coincide). */
export function ordenFicha(unidades: UnidadProyecto[], f: FiltroProyecto, elegida: number): number[] {
  const orden = [...unidades].sort(porPisoYNumero);
  const coinciden = orden.filter((u) => coincide(u, f));
  return (coinciden.some((u) => u.id === elegida) ? coinciden : orden).map((u) => u.id);
}

// ------------------------------------------------------------------ el sol de la ficha (Buenos Aires: el sol va por el norte)

const RUMBOS = ["Norte", "Noreste", "Este", "Sudeste", "Sur", "Sudoeste", "Oeste", "Noroeste"];
const SOL: Record<string, string> = {
  Norte: "Sol casi todo el día, todo el año: el más luminoso en invierno.",
  Noreste: "Sol de la mañana hasta media tarde, todo el año.",
  Este: "Sol de la mañana, todo el año.",
  Sudeste: "Sol temprano a la mañana, sobre todo en verano.",
  Sur: "Luz pareja, sin sol directo en invierno: el más fresco en verano.",
  Sudoeste: "Sol del final de la tarde, sobre todo en verano.",
  Oeste: "Sol de la tarde, todo el año: más cálido en verano.",
  Noroeste: "Sol del mediodía y de la tarde, todo el año.",
};

/** El más cercano de los ocho rumbos (0 N, 90 E…). */
export function rumbo(grados: number): string {
  return RUMBOS[Math.round((((grados % 360) + 360) % 360) / 45) % 8]!;
}

/** "Mira al Noreste" y qué sol tiene. Sin el rumbo del terreno cargado, null. */
export function solDe(miraA: number | null): { titulo: string; texto: string } | null {
  if (miraA === null) return null;
  const r = rumbo(miraA);
  return { titulo: `Mira al ${r}`, texto: SOL[r]! };
}

// ------------------------------------------------------------------ la presentación comercial

/**
 * La portada con renders y carpeta comercial es la de Newbery 3563. Se muestra salvo que el desarrollo tenga cargada
 * otra dirección (Mati, 2026-11-03: con la dirección vacía o escrita distinto, "3.563", no aparecía).
 */
export function esNewbery3563(direccion: string | null): boolean {
  const d = (direccion ?? "").trim();
  return d === "" || /newbery/i.test(d);
}

/**
 * Los pisos que son parte de la unidad de abajo. En Newbery 3563 (Mati, 2026-11-03) el 8 es la azotea del dúplex del 7:
 * no tiene unidades propias, y lo que haya cargado ahí (el depto de prueba) no se muestra.
 */
export function pisosAnexos(direccion: string | null): PisoAnexo[] {
  return esNewbery3563(direccion) ? [{ piso: 8, de: 7 }] : [];
}

/** Lo que se muestra de las unidades (Proyecto e Inicio): sin las cargadas en un piso anexo. */
export function unidadesVisibles<T extends Pick<UnidadProyecto, "piso">>(unidades: T[], anexos: PisoAnexo[]): T[] {
  return unidades.filter((u) => !anexos.some((a) => a.piso === u.piso));
}
